import type { Client } from '../store/types';
import { gridToSource, isoLocal, parseCsvText, normalizeDate, transposeVertical, ROW_CAP } from '@fitflex/shared';
import type { ParsedSource, Mapping, TargetField } from '@fitflex/shared';

/* ============================================================
   Client-import (web). The pure parsing/mapping engine lives in
   @fitflex/shared (shared with mobile). This file keeps the
   web-specific readers — xlsx (lazy-loaded so the ~400KB chunk
   only downloads on upload) and the browser File API — plus the
   Client-coupled assembly (buildImportRows / detectClientLog),
   since the Client shape differs between web and mobile.
============================================================ */

// Re-export the shared parsing engine so existing web imports keep working.
export {
  parseCsvText,
  detectHeaderRow,
  gridToSource,
  sheetLinkToGvizCsv,
  fetchSheetCsv,
  analyzeSource,
  guessMapping,
  transposeVertical,
  normalizeDate,
  isoLocal,
  TARGET_FIELDS,
  ROW_CAP,
  TEMPLATE_CSV,
} from '@fitflex/shared';
export type { ParsedSource, TargetField, Mapping, SourceAnalysis } from '@fitflex/shared';

export interface ImportRow {
  key: string;                 // stable key for React + retry bookkeeping
  draft: Partial<Client>;      // fields for the profiles insert
  note?: string;               // goes to coaching_notes, NOT the profile row
  duplicate: boolean;          // matches the existing roster (or earlier batch row)
  included: boolean;           // checkbox state (duplicates start unchecked)
  sourceFile?: string;
}

/* ---------- files (CSV + Excel) ---------- */

// Parse EVERY non-empty tab of a workbook buffer (xlsx lazy chunk).
export async function parseXlsxBuffer(buf: ArrayBuffer, fileName: string): Promise<ParsedSource[]> {
  const XLSX = await import('xlsx');
  // Explicit type — cross-realm ArrayBuffers (tests, workers) defeat sniffing.
  const wb = XLSX.read(new Uint8Array(buf), { type: 'array', cellDates: true });
  const toText = (v: unknown): string => {
    if (v == null) return '';
    if (v instanceof Date) return isoLocal(v);
    return String(v).trim();
  };
  const out: ParsedSource[] = [];
  for (const tabName of wb.SheetNames) {
    const aoa = XLSX.utils.sheet_to_json(wb.Sheets[tabName], { header: 1, raw: true, defval: '' }) as unknown[][];
    const grid = aoa.map((r) => r.map(toText)).filter((r) => r.some((c) => c !== ''));
    if (grid.length === 0) continue;
    const src = gridToSource(grid, fileName, wb.SheetNames.length > 1 ? tabName : undefined);
    if (src.headers.length > 0) out.push(src);
  }
  return out;
}

export async function parseWorkbookFile(file: File): Promise<ParsedSource[]> {
  const isCsv = /\.csv$/i.test(file.name) || file.type === 'text/csv';
  if (isCsv) {
    const { headers, rows } = parseCsvText(await file.text());
    const src = gridToSource([headers, ...rows], file.name);
    return src.headers.length ? [src] : [];
  }
  return parseXlsxBuffer(await file.arrayBuffer(), file.name);
}

/* ---------- per-client training-log workbooks ----------
   One FILE = one client (the owner's own format): dated workout tabs
   ("061026 - Lower") and/or a vertical info tab. We extract the client —
   full history import is a separate (roadmap) feature. */
export interface ClientLogDetection {
  name: string;
  draft: Partial<Client>;
}

export function detectClientLog(sources: ParsedSource[], fileName: string): ClientLogDetection | null {
  const datedTabs = sources.filter((s) => s.tabName && /^\d{6}\s*[-–]/.test(s.tabName.trim())).length;
  const first = sources[0];
  const vertical = first ? transposeVertical(first) : null;
  const logName = /log|training|journal/i.test(fileName);
  if (datedTabs < 2 && !(vertical && logName)) return null;

  let name = fileName
    .replace(/\.(xlsx|xls|csv)$/i, '')
    .replace(/(private\s+)?(training|workout|client)?\s*(log|journal|tracker)/gi, '')
    .replace(/[-_]+/g, ' ')
    .trim();
  if (!name) name = 'New Client';

  const draft: Partial<Client> = { name };
  if (vertical) {
    const find = (rx: RegExp) => {
      const i = vertical.headers.findIndex((h) => rx.test(h));
      return i >= 0 ? (vertical.rows[0][i] || '').trim() : '';
    };
    const weightish = find(/weight|body\s*fat|baseline/i);
    const goals = find(/goal|objective|focus/i);
    const start = normalizeDate(find(/start\s*date|^date\b|since|joined/i));
    const pieces = [weightish && `Baseline: ${weightish}`, goals].filter(Boolean);
    if (pieces.length) draft.goals = pieces.join('. ');
    if (start) draft.startDate = start;
  }
  return { name, draft };
}

/* ---------- assembling rows ---------- */

export function buildImportRows(
  sources: ParsedSource[],
  mapping: Mapping,
  existingClients: Pick<Client, 'name' | 'email'>[],
  // Unmapped columns the coach chose to keep — preserved verbatim in the note
  // ("Belt Rank: blue"), so their organization scheme survives the import.
  noteHeaders: string[] = []
): { rows: ImportRow[]; skipped: number; truncated: boolean } {
  const existingEmails = new Set(existingClients.map((c) => (c.email || '').trim().toLowerCase()).filter(Boolean));
  const existingNames = new Set(existingClients.map((c) => c.name.trim().toLowerCase()));
  const batchEmails = new Set<string>();
  const batchNames = new Set<string>();

  const rows: ImportRow[] = [];
  let skipped = 0;
  let truncated = false;
  let n = 0;

  for (const src of sources) {
    // Mapping stores header NAMES — resolve per source so multi-file imports
    // with different layouts still land in the right fields.
    const col = (field: TargetField): number => {
      const header = mapping[field];
      return header ? src.headers.indexOf(header) : -1;
    };
    const idx: Record<TargetField, number> = {
      name: col('name'), email: col('email'), phone: col('phone'),
      startDate: col('startDate'), goals: col('goals'), injuries: col('injuries'), note: col('note'),
    };
    const get = (row: string[], field: TargetField): string =>
      idx[field] >= 0 ? (row[idx[field]] || '').trim() : '';

    for (const raw of src.rows) {
      const name = get(raw, 'name');
      if (!name) { skipped++; continue; }
      // a header row repeated mid-file (common when sheets are pasted together)
      if (mapping.name && name === mapping.name) { skipped++; continue; }
      if (rows.length >= ROW_CAP) { truncated = true; break; }

      const email = get(raw, 'email');
      const emailKey = email.toLowerCase();
      const nameKey = name.toLowerCase();
      const duplicate = emailKey
        ? existingEmails.has(emailKey) || batchEmails.has(emailKey)
        : existingNames.has(nameKey) || batchNames.has(nameKey);
      if (emailKey) batchEmails.add(emailKey);
      batchNames.add(nameKey);

      const noteParts: string[] = [];
      const mappedNote = get(raw, 'note');
      if (mappedNote) noteParts.push(mappedNote);
      for (const h of noteHeaders) {
        const i = src.headers.indexOf(h);
        const v = i >= 0 ? (raw[i] || '').trim() : '';
        if (v) noteParts.push(`${h}: ${v}`);
      }
      const note = noteParts.join('\n');
      rows.push({
        key: `imp_${n++}`,
        draft: {
          name,
          email: email || undefined,
          phone: get(raw, 'phone') || undefined,
          startDate: normalizeDate(get(raw, 'startDate')),
          goals: get(raw, 'goals') || undefined,
          injuries: get(raw, 'injuries') || undefined,
        },
        note: note || undefined,
        duplicate,
        included: !duplicate,
        sourceFile: sources.length > 1 ? src.fileName : undefined,
      });
    }
  }
  return { rows, skipped, truncated };
}
